import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RevealBox, SectionLabel, SectionTitle } from './UI';

const questions = [
  {
    id: 'status',
    question: 'Which of the following best describes your current professional status?',
    emoji: '👤',
    options: [
      { label: 'Student / Under Graduate', value: 'student' },
      { label: 'Recent Graduate', value: 'graduate' },
      { label: 'Working Professional', value: 'working' },
      { label: 'Looking for Corporate / Management Upskilling', value: 'upskill' },
    ],
  },
  {
    id: 'interest',
    question: 'What are you interested in?',
    emoji: '🎯',
    options: [
      { label: 'MBA / Management Program', value: 'mba' },
      { label: 'BBA / B.com / Undergraduate Degree', value: 'ug' },
      { label: 'M.com / MA / Postgraduate Degree', value: 'pg' },
      { label: 'Professional Certification', value: 'cert' },
      { label: 'Not Sure — Need Guidance', value: 'unsure' },
    ],
  },
  {
    id: 'budget',
    question: "What's your monthly budget for the program?",
    emoji: '💰',
    options: [
      { label: 'Under ₹3000 / month', value: 'low' },
      { label: '₹3000 – ₹6000 / month', value: 'mid' },
      { label: '₹6000 – ₹10000 / month', value: 'high' },
      { label: 'Budget is not a concern', value: 'any' },
      { label: 'I need EMI / loan options', value: 'emi' },
    ],
  },
  {
    id: 'timeline',
    question: 'When are you planning to start?',
    emoji: '📅',
    options: [
      { label: 'Immediately / This month', value: 'now' },
      { label: 'Within 3 months', value: 'soon' },
      { label: 'Just Exploring', value: 'explore' },
    ],
  },
];

export default function CourseFinderQuiz() {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);

  const handleAnswer = (value) => {
    setSelected(value);
    setTimeout(() => {
      const newAnswers = { ...answers, [questions[current].id]: value };
      setAnswers(newAnswers);
      setSelected(null);
      if (current < questions.length - 1) {
        setCurrent(current + 1);
      } else {
        // Last question answered — show thank you then scroll to contact
        setDone(true);
        setTimeout(() => {
          const el = document.getElementById('contact');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 800);
      }
    }, 380);
  };

  const reset = () => {
    setCurrent(0);
    setAnswers({});
    setSelected(null);
    setDone(false);
  };

  const progress = done ? 100 : Math.round((current / questions.length) * 100);

  return (
    <section id="quiz" style={{ padding: '100px 6%', background: 'var(--navy)' }}>
      <RevealBox style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
        <SectionLabel dark>Course Finder</SectionLabel>
        <SectionTitle style={{ color: '#fff', maxWidth: '100%', textAlign: 'center' }}>
          Find Your <em style={{ fontStyle: 'italic', color: 'var(--yellow)' }}>Perfect Program</em>
        </SectionTitle>
        <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.6)', marginTop: 14, lineHeight: 1.7 }}>
          Answer 4 quick questions — we'll match you to the right degree in 60 seconds.
        </p>
      </RevealBox>

      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* Progress bar */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
              {done ? 'Complete!' : `Question ${current + 1} of ${questions.length}`}
            </span>
            <span style={{ fontSize: 13, color: 'var(--yellow)', fontWeight: 700 }}>
              {progress}% complete
            </span>
          </div>
          <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
            <motion.div
              animate={{ width: progress + '%' }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              style={{ height: '100%', background: 'var(--yellow)', borderRadius: 3 }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            {questions.map((_, i) => (
              <div key={i} style={{
                width: 10, height: 10, borderRadius: '50%',
                background: i < current || done ? 'var(--yellow)' : i === current ? '#fff' : 'rgba(255,255,255,0.15)',
                transition: 'all 0.3s',
                boxShadow: i === current && !done ? '0 0 0 3px rgba(255,255,255,0.2)' : 'none',
              }} />
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 20, padding: '44px 40px',
              }}>
                <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 20 }}>
                  {questions[current].emoji}
                </div>
                <h3 style={{
                  fontFamily: 'Playfair Display,serif',
                  fontSize: 'clamp(18px,3vw,24px)',
                  color: '#fff', textAlign: 'center', marginBottom: 32, lineHeight: 1.35,
                }}>
                  {questions[current].question}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {questions[current].options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(opt.value)}
                      style={{
                        background: selected === opt.value ? 'var(--yellow)' : 'rgba(255,255,255,0.05)',
                        border: '1.5px solid ' + (selected === opt.value ? 'var(--yellow)' : 'rgba(255,255,255,0.15)'),
                        borderRadius: 12, padding: '15px 20px',
                        color: selected === opt.value ? 'var(--navy)' : '#fff',
                        fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        textAlign: 'left', transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        fontFamily: 'DM Sans,sans-serif',
                      }}
                      onMouseEnter={e => {
                        if (selected !== opt.value) {
                          e.currentTarget.style.background = 'rgba(245,197,24,0.12)';
                          e.currentTarget.style.borderColor = 'rgba(245,197,24,0.4)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (selected !== opt.value) {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                        }
                      }}
                    >
                      <span>{opt.label}</span>
                      <span style={{ opacity: 0.4, fontSize: 16 }}>→</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            // Thank you screen — shows briefly before scroll
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 20, padding: '56px 40px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 56, marginBottom: 20 }}>🎉</div>
                <h3 style={{
                  fontFamily: 'Playfair Display,serif', fontSize: 28,
                  color: '#fff', marginBottom: 12,
                }}>Great! We've Got Your Answers.</h3>
                <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: 32 }}>
                  Our expert counsellors will match you to the perfect program.<br />
                  Taking you to the booking form now...
                </p>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  color: 'var(--yellow)', fontSize: 14, fontWeight: 600,
                }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    style={{ width: 18, height: 18, border: '2px solid var(--yellow)', borderTopColor: 'transparent', borderRadius: '50%' }}
                  />
                  Scrolling to contact form...
                </div>
                <button
                  onClick={reset}
                  style={{
                    display: 'block', margin: '24px auto 0',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'rgba(255,255,255,0.4)', fontSize: 13,
                    fontFamily: 'DM Sans,sans-serif', textDecoration: 'underline',
                  }}
                >
                  ↺ Retake Quiz
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}